import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    ArrowRight,
    FileText,
    Search as SearchIcon,
    MapPin,
    Calendar,
    Clock,
    Sparkles,
} from "lucide-react";
import { fetchMeta, fetchPrograms } from "../lib/api";

const TYPE_LABEL = {
    kommune: "Bergen kommune",
    fylkesting: "Vestland fylkesting",
    nasjonal: "Nasjonalt program (Stortinget)",
};
const TYPE_SHORT = { kommune: "Kommune", fylkesting: "Fylkesting", nasjonal: "Nasjonalt" };

// Heuristic: a period like "2023-2027" is "current" if today's year is within the range.
function periodStatus(period) {
    const m = period?.match(/(\d{4})\s*[–-]\s*(\d{4})/);
    if (!m) return "unknown";
    const start = parseInt(m[1], 10);
    const end = parseInt(m[2], 10);
    const y = new Date().getFullYear();
    if (y >= start && y <= end) return "current";
    if (y > end) return "historical";
    return "upcoming";
}

function periodSortKey(period) {
    const m = period?.match(/(\d{4})/);
    return m ? parseInt(m[1], 10) : 0;
}

export default function Home() {
    const navigate = useNavigate();
    const [q, setQ] = useState("");
    const [meta, setMeta] = useState({ periods: [], types: [], regions: [], count: 0 });
    const [programs, setPrograms] = useState([]);
    const [selectedPeriod, setSelectedPeriod] = useState("all");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchMeta().then(setMeta).catch(() => {});
    }, []);

    useEffect(() => {
        setLoading(true);
        fetchPrograms({})
            .then(setPrograms)
            .catch(() => setPrograms([]))
            .finally(() => setLoading(false));
    }, []);

    // Sorted unique periods (newest first)
    const periods = useMemo(() => {
        const set = new Set(programs.map((p) => p.period).filter(Boolean));
        return Array.from(set).sort((a, b) => periodSortKey(b) - periodSortKey(a));
    }, [programs]);

    // Auto-pick most recent period on first load if user has not chosen one
    useEffect(() => {
        if (selectedPeriod === "all" && periods.length > 0) {
            const current = periods.find((p) => periodStatus(p) === "current");
            setSelectedPeriod(current || periods[0]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [periods.length]);

    const submitSearch = (e) => {
        e.preventDefault();
        if (!q.trim()) return;
        const params = new URLSearchParams({ q: q.trim() });
        if (selectedPeriod && selectedPeriod !== "all") params.set("period", selectedPeriod);
        navigate(`/search?${params.toString()}`);
    };

    const visiblePrograms =
        selectedPeriod === "all"
            ? programs
            : programs.filter((p) => p.period === selectedPeriod);

    const byType = visiblePrograms.reduce((acc, p) => {
        (acc[p.type] ||= []).push(p);
        return acc;
    }, {});

    return (
        <main data-testid="home-page" className="flex-1">
            {/* Hero */}
            <section className="relative overflow-hidden border-b border-zinc-200 bg-white">
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 opacity-[0.06]"
                    style={{
                        backgroundImage:
                            "linear-gradient(to right, #09090b 1px, transparent 1px), linear-gradient(to bottom, #09090b 1px, transparent 1px)",
                        backgroundSize: "40px 40px",
                    }}
                />
                <div className="mx-auto max-w-7xl px-6 py-16 md:px-12 md:py-24">
                    <div className="grid grid-cols-1 gap-12 md:grid-cols-12">
                        <div className="md:col-span-8">
                            <div className="pp-overline mb-6 flex flex-wrap items-center gap-3">
                                <span className="inline-flex items-center gap-1.5 border border-[#C8102E] bg-[#C8102E] px-2 py-0.5 text-white">
                                    Rødt
                                </span>
                                <span>Bergen · Vestland · Stortinget</span>
                            </div>
                            <h1 className="font-heading text-5xl font-black leading-[0.95] tracking-tighter text-zinc-950 md:text-7xl">
                                Hva mener
                                <br />
                                <span className="text-[#C8102E]">Rødt</span>
                                <br />
                                egentlig?
                            </h1>
                            <p className="mt-8 max-w-2xl font-sans text-base leading-relaxed text-zinc-700 md:text-lg">
                                Et åpent arkiv over Rødt sine partiprogrammer
                                for Bergen kommune, Vestland fylkesting og
                                Stortinget. Bla i både gjeldende og historiske
                                programmer, søk i fritekst og del lenker rett
                                til avsnittet.
                            </p>

                            <form
                                onSubmit={submitSearch}
                                data-testid="hero-search-form"
                                className="mt-10 flex max-w-2xl items-stretch"
                            >
                                <div className="relative flex-1">
                                    <SearchIcon className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-zinc-500" />
                                    <input
                                        data-testid="hero-search-input"
                                        type="text"
                                        value={q}
                                        onChange={(e) => setQ(e.target.value)}
                                        placeholder="Søk f.eks. «bybane», «boligpolitikk», «klima»…"
                                        className="pp-search-input pl-12"
                                    />
                                </div>
                                <button
                                    data-testid="hero-search-submit"
                                    type="submit"
                                    className="ml-3 inline-flex items-center gap-2 border-2 border-zinc-950 bg-zinc-950 px-6 font-medium text-white transition-colors hover:bg-zinc-800"
                                    style={{ boxShadow: "4px 4px 0 0 rgba(9,9,11,1)" }}
                                >
                                    Søk <ArrowRight className="h-4 w-4" />
                                </button>
                            </form>
                        </div>

                        <aside className="md:col-span-4">
                            <div className="border border-zinc-200 bg-zinc-50 p-6">
                                <div className="pp-overline mb-3">Arkivet</div>
                                <dl className="space-y-3">
                                    <div className="flex items-baseline justify-between">
                                        <dt className="text-sm text-zinc-600">
                                            Programmer
                                        </dt>
                                        <dd
                                            data-testid="stat-programs"
                                            className="font-heading text-3xl font-black text-zinc-950"
                                        >
                                            {meta.count}
                                        </dd>
                                    </div>
                                    <div className="flex items-baseline justify-between">
                                        <dt className="text-sm text-zinc-600">
                                            Perioder
                                        </dt>
                                        <dd
                                            data-testid="stat-periods"
                                            className="font-heading text-3xl font-black text-zinc-950"
                                        >
                                            {meta.periods?.length || 0}
                                        </dd>
                                    </div>
                                    <div className="flex items-baseline justify-between">
                                        <dt className="text-sm text-zinc-600">
                                            Regioner
                                        </dt>
                                        <dd className="font-heading text-3xl font-black text-zinc-950">
                                            {(meta.types?.length || 0)}
                                        </dd>
                                    </div>
                                </dl>
                            </div>
                        </aside>
                    </div>
                </div>
            </section>

            {/* Period selector */}
            {periods.length > 0 && (
                <section
                    data-testid="period-selector-section"
                    className="border-b border-zinc-200 bg-zinc-50"
                >
                    <div className="mx-auto max-w-7xl px-6 py-8 md:px-12">
                        <div className="mb-4 flex items-baseline justify-between">
                            <h2 className="pp-overline flex items-center gap-2">
                                <Clock className="h-3.5 w-3.5" /> Velg periode
                            </h2>
                            <span className="text-xs text-zinc-500">
                                {periods.length} programperiode
                                {periods.length !== 1 ? "r" : ""} tilgjengelig
                            </span>
                        </div>
                        <div
                            data-testid="period-pills"
                            className="flex flex-wrap gap-2"
                        >
                            <PeriodPill
                                label="Alle perioder"
                                value="all"
                                active={selectedPeriod === "all"}
                                onClick={() => setSelectedPeriod("all")}
                            />
                            {periods.map((p) => {
                                const status = periodStatus(p);
                                return (
                                    <PeriodPill
                                        key={p}
                                        label={p}
                                        value={p}
                                        active={selectedPeriod === p}
                                        status={status}
                                        onClick={() => setSelectedPeriod(p)}
                                    />
                                );
                            })}
                        </div>
                    </div>
                </section>
            )}

            {/* Programs list */}
            <section className="mx-auto max-w-7xl px-6 py-12 md:px-12">
                {loading ? (
                    <div data-testid="programs-loading" className="font-mono text-sm text-zinc-500">
                        Laster…
                    </div>
                ) : programs.length === 0 ? (
                    <EmptyState />
                ) : visiblePrograms.length === 0 ? (
                    <div className="border border-dashed border-zinc-300 bg-zinc-50 p-12 text-center">
                        <h3 className="font-heading text-2xl font-bold text-zinc-950">
                            Ingen programmer i denne perioden
                        </h3>
                        <p className="mt-2 text-sm text-zinc-600">
                            Velg en annen periode i listen over.
                        </p>
                    </div>
                ) : (
                    <>
                        <div className="mb-8 flex items-baseline justify-between border-b border-zinc-200 pb-4">
                            <h2 className="font-heading text-3xl font-extrabold tracking-tight text-zinc-950 md:text-4xl">
                                {selectedPeriod === "all"
                                    ? "Alle programmer"
                                    : `Programmer · ${selectedPeriod}`}
                            </h2>
                            {selectedPeriod !== "all" && (
                                <PeriodStatusBadge status={periodStatus(selectedPeriod)} />
                            )}
                        </div>

                        {["nasjonal", "fylkesting", "kommune"].map((t) =>
                            byType[t]?.length ? (
                                <div key={t} className="mb-12">
                                    <h3 className="pp-overline mb-4">
                                        {TYPE_LABEL[t]} · {byType[t].length}{" "}
                                        program{byType[t].length !== 1 ? "mer" : ""}
                                    </h3>
                                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                                        {byType[t].map((p) => (
                                            <ProgramCard
                                                key={p.id}
                                                program={p}
                                            />
                                        ))}
                                    </div>
                                </div>
                            ) : null,
                        )}
                    </>
                )}
            </section>
        </main>
    );
}

function PeriodPill({ label, value, active, onClick, status }) {
    const base =
        "inline-flex items-center gap-1.5 border px-3 py-1.5 font-mono text-xs uppercase tracking-wider transition-all";
    const styleActive =
        "border-zinc-950 bg-zinc-950 text-white";
    const styleInactive =
        "border-zinc-300 bg-white text-zinc-700 hover:border-zinc-950 hover:text-zinc-950";
    return (
        <button
            data-testid={`period-pill-${value}`}
            onClick={onClick}
            className={`${base} ${active ? styleActive : styleInactive}`}
        >
            {status === "current" && <Sparkles className="h-3 w-3" />}
            {label}
            {status === "historical" && (
                <span className="ml-1 text-[10px] opacity-70">historisk</span>
            )}
            {status === "current" && (
                <span className="ml-1 text-[10px] opacity-70">gjeldende</span>
            )}
        </button>
    );
}

function PeriodStatusBadge({ status }) {
    if (status === "current") {
        return (
            <span className="inline-flex items-center gap-1 border border-emerald-300 bg-emerald-50 px-2.5 py-1 font-mono text-[11px] uppercase tracking-wider text-emerald-800">
                <Sparkles className="h-3 w-3" /> Gjeldende periode
            </span>
        );
    }
    if (status === "historical") {
        return (
            <span className="inline-flex items-center gap-1 border border-zinc-300 bg-zinc-100 px-2.5 py-1 font-mono text-[11px] uppercase tracking-wider text-zinc-700">
                <Clock className="h-3 w-3" /> Historisk
            </span>
        );
    }
    if (status === "upcoming") {
        return (
            <span className="inline-flex items-center gap-1 border border-klein bg-blue-50 px-2.5 py-1 font-mono text-[11px] uppercase tracking-wider text-klein">
                Kommende
            </span>
        );
    }
    return null;
}

function ProgramCard({ program }) {
    const status = periodStatus(program.period);
    return (
        <Link
            to={`/programs/${program.id}`}
            data-testid={`program-card-${program.id}`}
            className="pp-card group flex flex-col"
        >
            <div className="mb-4 flex items-start justify-between">
                <FileText className="h-6 w-6 text-zinc-400 group-hover:text-[#C8102E]" strokeWidth={2} />
                <span className="pp-tag">{TYPE_SHORT[program.type] || program.type}</span>
            </div>
            <h3 className="font-heading text-xl font-bold tracking-tight text-zinc-950">
                {program.region ||
                    (program.type === "kommune"
                        ? "Bergen"
                        : program.type === "fylkesting"
                          ? "Vestland"
                          : "Norge")}
            </h3>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-zinc-500">
                <span className="inline-flex items-center gap-1">
                    <Calendar className="h-3 w-3" /> {program.period}
                </span>
                {status === "current" && (
                    <span className="inline-flex items-center gap-1 text-emerald-700">
                        <Sparkles className="h-3 w-3" /> Gjeldende
                    </span>
                )}
                {status === "historical" && (
                    <span className="inline-flex items-center gap-1 text-zinc-500">
                        <Clock className="h-3 w-3" /> Historisk
                    </span>
                )}
            </div>
            {program.keywords?.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-1.5">
                    {program.keywords.slice(0, 5).map((k) => (
                        <span key={k} className="pp-tag normal-case">
                            {k}
                        </span>
                    ))}
                    {program.keywords.length > 5 && (
                        <span className="pp-tag">+{program.keywords.length - 5}</span>
                    )}
                </div>
            )}
            <div className="mt-auto pt-5 text-sm font-medium text-[#C8102E] group-hover:underline">
                Åpne program →
            </div>
        </Link>
    );
}

function EmptyState() {
    return (
        <div
            data-testid="empty-state"
            className="border border-dashed border-zinc-300 bg-zinc-50 p-12 text-center"
        >
            <h3 className="font-heading text-2xl font-bold text-zinc-950">
                Arkivet er tomt
            </h3>
            <p className="mx-auto mt-3 max-w-md text-sm text-zinc-600">
                Programmer som lastes opp av administrator vises her. Er du
                admin?{" "}
                <Link to="/admin" data-testid="empty-admin-link" className="pp-link">
                    Logg inn og last opp første program
                </Link>
                .
            </p>
        </div>
    );
}
